/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Loading from 'components/Loading';
import DashboardChart from 'components/Chart';
import { useGetProjectProposalsExpenditures } from 'utils/hooks/dashboard';
import { formatShowAppliedFilters } from 'utils/func/dashboard';
import { toMillion } from 'utils/func';
import DashboardFilter from '../DashboardFilter';

export default function ProjectExpenditureChart({ programs, organizations }) {
  const { t } = useTranslation();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [filters, setFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });

  const [showFilterDialog, setShowFilterDialog] = useState(false);

  const hasActiveFilters =
    appliedFilters.yearProposed?.length > 0 ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const { data, isLoading } = useGetProjectProposalsExpenditures(appliedFilters, {
    refetchOnWindowFocus: false,
    enabled: isLoadedFilterFromLocalStorage,
  });

  const documentStyle = getComputedStyle(document.documentElement);
  const textColor = documentStyle.getPropertyValue('--text-color');
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
  const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

  const chartData = useMemo(() => {
    if (data && Array.isArray(data)) {
      const labels = data.map((item) => item.yearProposed);
      const totalProposedFromVNU = data.map((item) => toMillion(item.totalProposedFromVNU));
      const totalProposedFromFunding = data.map((item) => toMillion(item.totalProposedFromFunding));
      const totalApprovedFromVNU = data.map((item) => toMillion(item.totalApprovedFromVNU));
      const totalApprovedFromFunding = data.map((item) => toMillion(item.totalApprovedFromFunding));

      return {
        labels,
        datasets: [
          {
            label: t('dashboard.expenditure.label.totalProposedFromVNU'),
            backgroundColor: documentStyle.getPropertyValue('--green-400'),
            data: totalProposedFromVNU,
          },
          {
            label: t('dashboard.expenditure.label.totalApprovedFromVNU'),
            backgroundColor: documentStyle.getPropertyValue('--green-500'),
            data: totalApprovedFromVNU,
          },
          {
            label: t('dashboard.expenditure.label.totalProposedFromFunding'),
            backgroundColor: documentStyle.getPropertyValue('--blue-400'),
            data: totalProposedFromFunding,
          },
          {
            label: t('dashboard.expenditure.label.totalApprovedFromFunding'),
            backgroundColor: documentStyle.getPropertyValue('--blue-500'),
            data: totalApprovedFromFunding,
          },
        ],
      };
    }
    return null;
  }, [data, t]);

  const options = useMemo(
    () => ({
      //   indexAxis: 'y',
      maintainAspectRatio: false,
      aspectRatio: 0.5,
      plugins: {
        legend: {
          labels: {
            color: textColor,
            generateLabels(chart) {
              const { data: _data } = chart;
              return _data.datasets.map((dts, i) => {
                const value = dts.data.reduce((sum, val) => sum + val, 0);
                return {
                  text: `${dts.label} (${value.toLocaleString()})`,
                  fillStyle: dts.backgroundColor,
                  strokeStyle: dts.borderColor || dts.backgroundColor,
                  lineWidth: 1,
                  datasetIndex: i,
                  hidden: !chart.isDatasetVisible(i),
                };
              });
            },
          },
          position: 'bottom',
        },
        datalabels: {
          formatter: (value) => (value ? value.toLocaleString() : ''),
          font: {
            size: 12,
          },
          anchor: 'end',
          align: 'end',
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              weight: 500,
            },
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
          },
        },
      },
    }),
    [data]
  );

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-expenditrue-filters');
    if (!savedFilters) return;

    const parsed = JSON.parse(savedFilters);
    setFilters(parsed);
    setAppliedFilters(parsed);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div className="card p-3 md:p-5">
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.expenditure.title')}
          </h3>
          <span>{t('dashboard.expenditure.unit')}</span>
          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        isSelectOne={{
          yearProposed: false,
          organizationId: false,
          programId: false,
        }}
        localStorageKey="admin-dashboard-projects-expenditrue-filters"
      />

      <div className="flex flex-column align-items-center md:px-4 md:pb-4 gap-2">
        {formatShowAppliedFilters(appliedFilters, organizations, programs, t)}
        <DashboardChart type="bar" data={chartData} options={options} className="w-full" />
      </div>
    </div>
  );
}

ProjectExpenditureChart.propTypes = {
  programs: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};
